import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import axios from 'axios';
import { API_URL } from '../../config';
import { observer } from 'mobx-react-lite';
import ozonOrdersStore from '../../stores/OzonOrdersStore';

const ICONS = {
    home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    stock:
        'M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4',
    orders: 'M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z',
    logout:
        'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
    mark: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
    gpt: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
    profile: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    cost: 'M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z',
    reviews:
        'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
    promo: 'M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7',
};

const Icon = ({ d, className = 'h-5 w-5' }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={d} />
    </svg>
);

const isMobileViewport = () =>
    typeof window !== 'undefined' &&
    (window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));

const Sidebar = () => {
    const { isOpen, setIsOpen } = useSidebar();
    const { logout } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [expandedMenus, setExpandedMenus] = useState(() => {
        try {
            const raw = localStorage.getItem('bansel_sidebar_expanded');
            const parsed = raw ? JSON.parse(raw) : {};
            return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    });
    const [mobile, setMobile] = useState(isMobileViewport);
    const isFirstRender = useRef(true);
    const collapsed = !isOpen && !mobile;

    const menuItems = [
        {
            title: 'Главная',
            path: '/dashboard',
            icon: ICONS.home,
        },
        {
            title: 'Остатки',
            path: '/stock',
            icon: ICONS.stock,
            hasSubmenu: true,
            children: [
                { title: 'Склад', action: 'stock' },
                { title: 'Возвраты', path: '/stock/returns' },
                { title: 'Синхронизация Ozon', path: '/ozon/sync' },
            ],
        },
        {
            title: 'Честный знак',
            path: '/честный-знак',
            icon: ICONS.mark,
            hasSubmenu: true,
        },
        {
            title: 'BananasGPT',
            path: '/bananas-gpt',
            icon: ICONS.gpt,
            hasSubmenu: true,
            children: [
                { title: 'OpenRouter', path: '/bananas-gpt/openrouter' },
                { title: 'NanoGPT', path: '/bananas-gpt/nanogpt' },
            ],
        },
        {
            title: 'Заказы Ozon',
            path: '/orders/ozon',
            icon: ICONS.orders,
        },
        {
            title: 'Акции',
            path: '/actions/ozon',
            icon: ICONS.promo,
            hasSubmenu: true,
            children: [
                { title: 'Акции OZON', path: '/actions/ozon' },
                { title: 'Цены', path: '/actions/prices' },
            ],
        },
        {
            title: 'Отзывы',
            path: '/reviews',
            icon: ICONS.reviews,
            hasSubmenu: true,
            children: [
                { title: 'Список', path: '/reviews' },
                { title: 'Шаблоны ответов', path: '/reviews/templates' },
                { title: 'AI Learn', path: '/reviews/ai-learn' },
            ],
        },
        {
            title: 'Себестоимость',
            path: '/cost',
            icon: ICONS.cost,
            hasSubmenu: true,
            children: [
                { title: 'Расчёт', path: '/cost' },
                { title: 'Материалы', path: '/cost/materials' },
            ],
        },
        {
            title: 'Профиль',
            path: '/profile',
            icon: ICONS.profile,
        },
    ];

    const fetchArticles = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/v1/TrueMark/getMenu`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.data && Array.isArray(response.data)) {
                setArticles(response.data);
            }
        } catch (error) {
            console.error('Error fetching articles:', error);
        }
    }, []);

    useEffect(() => {
        const onResize = () => setMobile(isMobileViewport());
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    useEffect(() => {
        if (
            location.pathname === '/stock' ||
            location.pathname === '/stocksm' ||
            location.pathname.startsWith('/stock/') ||
            location.pathname.startsWith('/ozon/sync')
        ) {
            setExpandedMenus((prev) => (prev.Остатки ? prev : { ...prev, Остатки: true }));
        }
        if (location.pathname.startsWith('/reviews')) {
            setExpandedMenus((prev) => (prev.Отзывы ? prev : { ...prev, Отзывы: true }));
        }
        if (location.pathname.startsWith('/cost')) {
            setExpandedMenus((prev) =>
                prev.Себестоимость ? prev : { ...prev, Себестоимость: true }
            );
        }
        if (location.pathname.startsWith('/actions')) {
            setExpandedMenus((prev) => (prev.Акции ? prev : { ...prev, Акции: true }));
        }
        if (location.pathname.startsWith('/bananas-gpt')) {
            setExpandedMenus((prev) =>
                prev.BananasGPT ? prev : { ...prev, BananasGPT: true }
            );
        }
    }, [location.pathname]);

    useEffect(() => {
        try {
            localStorage.setItem('bansel_sidebar_expanded', JSON.stringify(expandedMenus));
        } catch {
            /* ignore */
        }
    }, [expandedMenus]);

    useEffect(() => {
        if ((isOpen || collapsed) && isFirstRender.current) {
            fetchArticles();
            isFirstRender.current = false;
        }
    }, [isOpen, collapsed, fetchArticles]);

    const handleLogout = () => {
        logout();
    };

    const goStock = () => {
        navigate(isMobileViewport() ? '/stocksm' : '/stock');
    };

    const toggleSubmenu = (title) => {
        setExpandedMenus((prev) => ({
            ...prev,
            [title]: !prev[title],
        }));
    };

    const handleArticleClick = (article) => {
        navigate('/честный-знак', { state: { articleFilter: article } });
    };

    const { hoba, buff, total: uncheckedHobaBuffs } = ozonOrdersStore.getUncheckedHobaBuffs();
    const hobaBuffBadge = `${hoba}/${buff}`;

    const isActive = (item) => {
        if (item.path === '/orders/ozon') return location.pathname.startsWith('/orders/ozon');
        if (item.path === '/bananas-gpt') return location.pathname.startsWith('/bananas-gpt');
        if (item.path === '/stock') {
            return (
                location.pathname === '/stock' ||
                location.pathname === '/stocksm' ||
                location.pathname.startsWith('/stock/') ||
                location.pathname.startsWith('/ozon/sync')
            );
        }
        if (item.path === '/reviews') return location.pathname.startsWith('/reviews');
        if (item.path === '/cost') return location.pathname.startsWith('/cost');
        if (item.path === '/actions/ozon') return location.pathname.startsWith('/actions');
        if (item.path === '/ozon/sync') return location.pathname.startsWith('/ozon/sync');
        return location.pathname === item.path;
    };

    const railItemClass = (active) =>
        `flex h-11 w-full items-center justify-center gap-0 rounded-xl transition-[padding,gap,justify-content] duration-200 group-hover/rail:justify-start group-hover/rail:gap-3 group-hover/rail:px-3 ${
            active ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700 hover:text-white'
        }`;

    const railLabelClass =
        'hidden min-w-0 truncate whitespace-nowrap text-sm font-medium group-hover/rail:inline';

    const fullItemClass = (active) =>
        `flex w-full items-center px-4 py-2 text-sm ${
            active ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700'
        }`;

    const renderBadge = (compact) => {
        if (uncheckedHobaBuffs <= 0) return null;
        if (compact) {
            return (
                <span className="absolute -right-2 -top-2 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-orange-500 px-1 text-[10px] font-semibold text-white group-hover/rail:hidden">
                    {uncheckedHobaBuffs > 9 ? '9+' : uncheckedHobaBuffs}
                </span>
            );
        }
        return (
            <span
                style={{
                    background: '#f50',
                    color: 'white',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: '12px',
                    marginLeft: '8px',
                    fontWeight: 600,
                }}
                title="Непроверенные Хобы/Баффы"
            >
                {hobaBuffBadge}
            </span>
        );
    };

    const renderMenuItems = ({ rail }) =>
        menuItems.map((item) => {
            const active = isActive(item);
            const key = item.path || item.title;

            if (item.hasSubmenu) {
                if (rail) {
                    return (
                        <li key={key}>
                            <button
                                type="button"
                                title={item.title}
                                onClick={() => {
                                    setIsOpen(true);
                                    toggleSubmenu(item.title);
                                    if (item.title === 'Остатки') {
                                        goStock();
                                        return;
                                    }
                                    if (location.pathname !== item.path) {
                                        navigate(item.path);
                                    }
                                }}
                                className={railItemClass(active)}
                            >
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                    <Icon d={item.icon} />
                                </span>
                                <span className={railLabelClass}>{item.title}</span>
                            </button>
                        </li>
                    );
                }

                return (
                    <li key={key}>
                        <button
                            type="button"
                            onClick={() => toggleSubmenu(item.title)}
                            className={`flex w-full items-center justify-between px-4 py-2 text-sm ${
                                active ? 'bg-gray-700 text-white' : 'text-gray-300 hover:bg-gray-700'
                            }`}
                        >
                            <div className="flex items-center">
                                <Icon d={item.icon} className="mr-3 h-5 w-5" />
                                {item.title}
                            </div>
                            <svg
                                className={`h-4 w-4 transition-transform ${
                                    expandedMenus[item.title] ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M19 9l-7 7-7-7"
                                />
                            </svg>
                        </button>
                        {expandedMenus[item.title] && (
                            <ul className="mt-2 space-y-1 pl-4">
                                {item.children ? (
                                    item.children.map((child) => (
                                        <li key={child.path || child.title}>
                                            {child.action === 'stock' ? (
                                                <button
                                                    type="button"
                                                    onClick={goStock}
                                                    className={`flex w-full items-center px-4 py-2 text-sm ${
                                                        location.pathname === '/stock' ||
                                                        location.pathname === '/stocksm'
                                                            ? 'bg-gray-700 text-white'
                                                            : 'text-gray-300 hover:bg-gray-700'
                                                    }`}
                                                >
                                                    {child.title}
                                                </button>
                                            ) : (
                                                <Link
                                                    to={child.path}
                                                    className={`flex w-full items-center px-4 py-2 text-sm ${
                                                        (
                                                            child.path === '/reviews' || child.path === '/cost'
                                                                ? location.pathname === child.path
                                                                : location.pathname === child.path ||
                                                                  location.pathname.startsWith(
                                                                      `${child.path}/`
                                                                  )
                                                        )
                                                            ? 'bg-gray-700 text-white'
                                                            : 'text-gray-300 hover:bg-gray-700'
                                                    }`}
                                                >
                                                    {child.title}
                                                </Link>
                                            )}
                                        </li>
                                    ))
                                ) : (
                                    <>
                                        <li>
                                            <button
                                                type="button"
                                                onClick={() => handleArticleClick('-')}
                                                className="flex w-full items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                                            >
                                                Все записи
                                            </button>
                                        </li>
                                        {articles.map((article) => (
                                            <li key={article}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleArticleClick(article)}
                                                    className="flex w-full items-center px-4 py-2 text-sm text-gray-300 hover:bg-gray-700"
                                                >
                                                    {article}
                                                </button>
                                            </li>
                                        ))}
                                    </>
                                )}
                            </ul>
                        )}
                    </li>
                );
            }

            if (item.title === 'Остатки') {
                if (rail) {
                    return (
                        <li key={key}>
                            <button type="button" title={item.title} onClick={goStock} className={railItemClass(active)}>
                                <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                                    <Icon d={item.icon} />
                                </span>
                                <span className={railLabelClass}>{item.title}</span>
                            </button>
                        </li>
                    );
                }
                return (
                    <li key={key}>
                        <button type="button" onClick={goStock} className={fullItemClass(active)}>
                            <Icon d={item.icon} className="mr-3 h-5 w-5" />
                            {item.title}
                        </button>
                    </li>
                );
            }

            if (rail) {
                return (
                    <li key={key}>
                        <Link
                            to={item.path}
                            title={
                                item.path === '/orders/ozon' && uncheckedHobaBuffs > 0
                                    ? `Заказы Ozon · непроверенные Хобы/Баффы: ${hobaBuffBadge}`
                                    : item.title
                            }
                            className={railItemClass(active)}
                        >
                            <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                                <Icon d={item.icon} />
                                {item.path === '/orders/ozon' && renderBadge(true)}
                            </span>
                            <span
                                className={`${railLabelClass} items-center gap-2 group-hover/rail:inline-flex`}
                            >
                                {item.title}
                                {item.path === '/orders/ozon' && uncheckedHobaBuffs > 0 && (
                                    <span className="rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                                        {hobaBuffBadge}
                                    </span>
                                )}
                            </span>
                        </Link>
                    </li>
                );
            }

            return (
                <li key={key}>
                    <Link to={item.path} className={fullItemClass(active)}>
                        <Icon d={item.icon} className="mr-3 h-5 w-5" />
                        {item.title}
                        {item.path === '/orders/ozon' && renderBadge(false)}
                    </Link>
                </li>
            );
        });

    // Desktop collapsed: icon rail, expand labels on hover
    if (collapsed) {
        return (
            <div className="group/rail fixed inset-y-0 left-0 z-[999] flex w-14 flex-col border-r border-gray-700/80 bg-gray-800 py-3 text-white shadow-xl transition-[width] duration-300 ease-out hover:w-56 hover:shadow-2xl">
                <div className="mb-2 flex h-11 items-center justify-center px-1.5 group-hover/rail:justify-between group-hover/rail:px-3">
                    <span className="hidden truncate text-sm font-semibold group-hover/rail:inline">BananaSeller</span>
                    <button
                        type="button"
                        onClick={() => setIsOpen(true)}
                        className="rounded-full p-1.5 text-gray-300 hover:bg-gray-700 hover:text-white focus:outline-none"
                        aria-label="Развернуть меню"
                        title="Развернуть меню"
                    >
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5l7 7-7 7"
                            />
                        </svg>
                    </button>
                </div>
                <nav className="flex flex-1 flex-col gap-1.5 overflow-y-auto px-1.5">
                    <ul className="space-y-1.5">{renderMenuItems({ rail: true })}</ul>
                </nav>
                <div className="mt-auto border-t border-gray-700/80 px-1.5 pt-2">
                    <button type="button" onClick={handleLogout} title="Выход" className={railItemClass(false)}>
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                            <Icon d={ICONS.logout} />
                        </span>
                        <span className={railLabelClass}>Выход</span>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            {mobile && (
                <div
                    className={`fixed top-4 left-4 z-[9999] transition-all duration-300 ease-in-out ${
                        isOpen ? 'translate-x-64 opacity-0 pointer-events-none' : 'translate-x-0 opacity-100'
                    }`}
                >
                    <button
                        type="button"
                        onClick={() => setIsOpen(true)}
                        className="rounded-full bg-gray-800 p-2 text-white shadow-lg hover:bg-gray-700 focus:outline-none"
                        aria-label="Открыть меню"
                    >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M4 6h16M4 12h16M4 18h16"
                            />
                        </svg>
                    </button>
                </div>
            )}

            <div
                className={`fixed inset-y-0 left-0 z-[999] w-64 bg-gray-800 text-white transition-transform duration-300 ease-in-out ${
                    mobile ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'
                }`}
            >
                <div className="flex items-center justify-between border-b border-gray-700 p-4">
                    <h2 className="text-xl font-semibold">BananaSeller</h2>
                    <button
                        type="button"
                        onClick={() => setIsOpen(false)}
                        className="rounded-full p-1 hover:bg-gray-700 focus:outline-none"
                        aria-label="Свернуть меню"
                    >
                        <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M15 19l-7-7 7-7"
                            />
                        </svg>
                    </button>
                </div>

                <nav className="mt-5">
                    <ul className="space-y-2">
                        {renderMenuItems({ rail: false })}
                        <li>
                            <button type="button" onClick={handleLogout} className={fullItemClass(false)}>
                                <Icon d={ICONS.logout} className="mr-3 h-5 w-5" />
                                Выход
                            </button>
                        </li>
                    </ul>
                </nav>
            </div>
        </>
    );
};

export default observer(Sidebar);
